import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";
import routes from "../../routes";
import NotFound from "../../Views/NotFound/NotFound";
import Sidebar from "../Sidebar";
import { MainNav } from "../MuiTable";
import { useMediaQuery } from "@mui/material";
import AxiosInstance from "../../utils/axiosInstance";
import { Grid } from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import {
  AdminDarkModeProvider,
  useAdminDarkMode,
} from "../../contexts/AdminDarkModeContext";
import { updateCompanyNameFromToken, setPageTitle, getPageTitleFromRoute } from "../../utils/pageTitle";
import {
  CJ_PORTAL_SIDEBAR_COLLAPSED_PX,
  CJ_PORTAL_SIDEBAR_EXPANDED_PX,
} from "../../utils/cjPortalSidebarMetrics";
import { getItem } from "../../utils/storage";
import "./admin-dark-overrides.css";

function StaffMemberInner() {
  const { darkMode } = useAdminDarkMode();
  const mainContent = useRef(null);
  const location = useLocation();
  const [isSidebarClosed, setIsSidebarClosed] = useState(false);
  const [isSidebarDisplay, setIsSidebarDisplay] = useState(true);
  const isMediumScreen = useMediaQuery("(max-width:767px)", { noSsr: true });
  const { CompanyUrl } = useParams();
  const baseUrl = import.meta.env.VITE_BASE_API;
  const [staffData, setStaffData] = useState(undefined);
  const [routesData, setRoutesData] = useState([]);

  // Reset sidebar state when screen size changes
  useEffect(() => {
    if (!isMediumScreen) {
      setIsSidebarDisplay(true);
      setIsSidebarClosed(false);
    } else {
      setIsSidebarDisplay(false);
    }
  }, [isMediumScreen]);

  useEffect(() => {
    // JOB-3245: updateCompanyNameFromToken is handled by ProtectedRoute; don't
    // repeat it here. Just set the title synchronously.
    const title = getPageTitleFromRoute(location.pathname);
    setPageTitle(title);
  }, []);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
    if (mainContent.current) {
      mainContent.current.scrollTop = 0;
    }

    const title = getPageTitleFromRoute(location.pathname);
    setPageTitle(title);
  }, [location]);

  useEffect(() => {
    if (darkMode) {
      document.body.classList.add("admin-dark-active");
    } else {
      document.body.classList.remove("admin-dark-active");
    }
    return () => document.body.classList.remove("admin-dark-active");
  }, [darkMode]);

  const workerPortalTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: darkMode ? "dark" : "light",
          primary: {
            main: darkMode ? "#90caf9" : "#1976d2",
          },
          secondary: {
            main: darkMode ? "#f48fb1" : "#dc004e",
          },
          background: {
            default: darkMode ? "#0f1419" : "#f7f8fa",
            paper: darkMode ? "#1a2332" : "#ffffff",
          },
          ...(darkMode
            ? {
                text: {
                  primary: "#e8eaed",
                  secondary: "#9aa0a6",
                },
                divider: "#2d3b4e",
              }
            : {}),
        },
        components: {
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundImage: "none",
              },
            },
          },
        },
        typography: {
          fontFamily: "'Poppins', sans-serif",
          h1: {
            fontFamily: "'Poppins', sans-serif",
            fontWeight: 600,
          },
          h2: {
            fontFamily: "'Poppins', sans-serif",
            fontWeight: 500,
          },
          body1: {
            fontFamily: "'Poppins', sans-serif",
          },
        },
      }),
    [darkMode]
  );

  useEffect(() => {
    const fetchStaffData = async () => {
      try {
        const response = await AxiosInstance.get(
          `/v1/worker/get/${getItem("worker_id")}`
        );
        setStaffData(response?.data?.data?.permissions);
      } catch (error) {
        console.error("Error fetching staff data:", error);
      }
    };

    if (location.pathname.includes("/staff-member")) {
      fetchStaffData();
    }
  }, [baseUrl]);

  useEffect(() => {
    if (staffData) {
      const filterRoutes = () => {
        const data = routes.filter(
          (item) =>
            item.layout === `/:CompanyUrl/staff-member` && item.isDisplay
        );

        const offModule = {
          ViewOnly: false,
          ViewCreateAndEdit: false,
          ViewCreateEditAndDelete: false,
        };
        const permissionChecks = {
          Quotes: staffData?.Quotes,
          Contract: staffData?.Contract,
          Invoice: staffData?.Invoice,
          Schedule: staffData?.Schedule,
          Appoinment: staffData?.Appoinment,
          CustomersProperties: staffData?.CustomersProperties,
          Customers: staffData?.Customers,
          Reports: staffData?.Reports,
          TimeTrackingAndTimesheets: staffData?.TimeTrackingAndTimesheets,
          MaterialsLabor: staffData?.MaterialsLabor,
          ServiceRequests: { ...offModule, ...staffData?.ServiceRequests },
        };

        const hasNoPermissions = (permissions) => 
          permissions && Object.values(permissions).every(value => !value);

        const pathToPermissionMap = {
          "/worker-quote": "Quotes",
          "/worker-service-requests": "ServiceRequests",
          "/worker-customer": "Customers",
          "/worker-schedule": "Schedule",
          "/worker-contract": "Contract",
          "/worker-invoice": "Invoice",
          "/worker-materials-labor": "MaterialsLabor",
          "/worker-appointments": "Appoinment",
          "/worker-todays-appointments": "Appoinment",
          "/worker-time-entry": "TimeTrackingAndTimesheets",
          "/worker-reports": "Reports"
        };

        const newRoutes = data.filter(item => {
          if (item.path === "/index") return true;
          
          const permission = pathToPermissionMap[item?.path];
          
          // If Customers permission is completely off, hide the customer panel
          if (item?.path === "/worker-customer") {
            const customersPermission = permissionChecks["Customers"];
            
            if (hasNoPermissions(customersPermission)) {
              return false;
            }
            
            // Show the panel if Customers permission has at least one permission enabled
            return true;
          }
          
          // Time Entry - show if any TimeTrackingAndTimesheets permission is enabled
          if (item?.path === "/worker-time-entry") {
            const timeTrackingPermission = permissionChecks["TimeTrackingAndTimesheets"];
            
            if (hasNoPermissions(timeTrackingPermission)) {
              return false;
            }
            
            // Show the panel if TimeTrackingAndTimesheets permission has at least one permission enabled
            return true;
          }
          
          return permission && !hasNoPermissions(permissionChecks[permission]);
        });

        setRoutesData(newRoutes);
      };
      filterRoutes();
    }
  }, [staffData]);

  const getRoutes = (routes) => {
    return routes?.map((prop, key) => {
      if (prop?.layout === "/:CompanyUrl/staff-member" && !prop?.isCollapse) {
        return (
          <Route path={prop?.path} element={prop?.component} key={key} exact />
        );
      } else if (prop?.children) {
        return prop?.children?.map((item) => (
          <Route
            path={item?.path}
            element={item?.component}
            key={key}
            exact
          />
        ));
      }
      return null;
    });
  };

  return (
    <ThemeProvider theme={workerPortalTheme}>
    <Grid
      className={`admin-shell${darkMode ? " admin-shell--dark" : ""}`}
      style={{
        backgroundColor: "var(--app-page-background)",
        width: "100vw",
        height: "100vh",
        overflow: "auto",
        overflowX: "hidden",
      }}
    >
      <Sidebar
        layout={`/:CompanyUrl/staff-member`}
        isSidebarClosed={isSidebarClosed}
        setIsSidebarClosed={setIsSidebarClosed}
        isSidebarDisplay={isSidebarDisplay}
        setIsSidebarDisplay={setIsSidebarDisplay}
        routesData={routesData}
      />

      <MainNav
        layout="/:CompanyUrl/staff-member"
        isSidebarDisplay={isSidebarDisplay}
        setIsSidebarDisplay={setIsSidebarDisplay}
        isSidebarClosed={isSidebarClosed}
      />
      <Grid
        className="main-content"
        ref={mainContent}
        sx={{
          boxSizing: "border-box",
          overflowX: "hidden",
          marginLeft: !isMediumScreen
            ? isSidebarClosed
              ? `${CJ_PORTAL_SIDEBAR_COLLAPSED_PX}px`
              : `${CJ_PORTAL_SIDEBAR_EXPANDED_PX}px`
            : 0,
          width: !isMediumScreen
            ? `calc(100% - ${
                isSidebarClosed
                  ? CJ_PORTAL_SIDEBAR_COLLAPSED_PX
                  : CJ_PORTAL_SIDEBAR_EXPANDED_PX
              }px)`
            : "100%",
          maxWidth: !isMediumScreen
            ? `calc(100% - ${
                isSidebarClosed
                  ? CJ_PORTAL_SIDEBAR_COLLAPSED_PX
                  : CJ_PORTAL_SIDEBAR_EXPANDED_PX
              }px)`
            : "100%",
          minWidth: 0,
          // Match Admin layout gutters: use a centered inner wrapper on desktop,
          // keep lightweight padding on small screens.
          px: { xs: "15px", sm: "15px", md: 0 },
          pb: 2,
        }}
      >
        <Grid
          sx={{
            width: { xs: "100%", md: "98%" },
            maxWidth: "100%",
            boxSizing: "border-box",
            minWidth: 0,
            mx: { xs: 0, md: "auto" },
          }}
        >
          <React.Suspense fallback={null}>
            <Routes>
              {getRoutes(routes)}
              {/* JOB-3220 + JOB-3225: real 404 instead of silent /index redirect */}
              <Route path="*" element={<NotFound homePath={`/${CompanyUrl}/staff-member/index`} />} />
            </Routes>
          </React.Suspense>
        </Grid>
      </Grid>
    </Grid>
    </ThemeProvider>
  );
}

const StaffMember = () => (
  <AdminDarkModeProvider>
    <StaffMemberInner />
  </AdminDarkModeProvider>
);

export default StaffMember;
